// Cada token só vale para a rota da sua action. Sem esse vínculo, qualquer
// request que passe pelo interceptor (polling de notificações/processos)
// consome o token pendente antes do destino chegar a usá-lo.
const ACTION_PATHS = {
  login: /auth\/sign_in/
}

// Token v3 expira em 2min.
const TOKEN_TTL = 110 * 1000

// grecaptcha carregado e ocioso por muito tempo (aba aberta de um dia para o
// outro, máquina suspensa) devolve token que o Google recusa com browser-error.
const STALE_AFTER = 30 * 60 * 1000

let scriptPromise = null
let pendingToken = null
let lastExecuteAt = 0

function getSiteKey() {
  return process.env.VUE_APP_RECAPTCHA_SITE_KEY
}

function loadScript(siteKey) {
  if (scriptPromise) return scriptPromise

  scriptPromise = new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('window unavailable'))
      return
    }

    if (window.grecaptcha && window.grecaptcha.execute) {
      window.grecaptcha.ready(() => resolve(window.grecaptcha))
      return
    }

    const script = document.createElement('script')
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`
    script.async = true
    script.defer = true
    script.onload = () => {
      if (!window.grecaptcha) {
        reject(new Error('grecaptcha not available after script load'))
        return
      }
      window.grecaptcha.ready(() => resolve(window.grecaptcha))
    }
    script.onerror = () => {
      scriptPromise = null
      reject(new Error('Failed to load reCAPTCHA script'))
    }
    document.head.appendChild(script)
  })

  return scriptPromise
}

// Descarta o cliente reCAPTCHA para que o próximo execute() reinjete o script.
// Sem remover o ___grecaptcha_cfg o script recarregado não se reinicializa.
export function reset() {
  scriptPromise = null
  pendingToken = null

  if (typeof window === 'undefined') return

  document.querySelectorAll('script[src*="recaptcha/api.js"], .grecaptcha-badge')
    .forEach(el => el.remove())
  delete window.grecaptcha
  delete window.___grecaptcha_cfg
}

export function actionForUrl(url) {
  return Object.keys(ACTION_PATHS).find(action => ACTION_PATHS[action].test(url || ''))
}

export async function execute(action) {
  if (!ACTION_PATHS[action])
    throw new Error(`[recaptcha] action "${action}" não mapeada em ACTION_PATHS`)

  const siteKey = getSiteKey()
  if (!siteKey) {
    console.warn('[recaptcha] VUE_APP_RECAPTCHA_SITE_KEY not configured; skipping token generation')
    pendingToken = null
    return null
  }

  if (lastExecuteAt && Date.now() - lastExecuteAt > STALE_AFTER) reset()
  lastExecuteAt = Date.now()

  try {
    const grecaptcha = await loadScript(siteKey)
    const token = await grecaptcha.execute(siteKey, { action })
    pendingToken = { token, action, createdAt: Date.now() }
    return token
  } catch (err) {
    reset()
    throw err
  }
}

export function consumePendingRecaptcha(url) {
  if (!pendingToken) return null

  if (Date.now() - pendingToken.createdAt > TOKEN_TTL) {
    pendingToken = null
    return null
  }

  if (!ACTION_PATHS[pendingToken.action].test(url || '')) return null

  const current = pendingToken
  pendingToken = null
  return current
}

export default {
  install(Vue) {
    Vue.prototype.$recaptcha = function (action) {
      return execute(action)
    }
  }
}
